import React from 'react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { ContactForm } from '../components/ContactForm';
import { companyInfo } from '../data/content';
import { MapPin, Phone, Mail, Clock } from 'lucide-react';
import { Toaster } from '../components/ui/sonner';

const ContactPage = () => {
  return (
    <div data-testid="contact-page" className="bg-white">
      <Navbar />
      <Toaster position="top-right" richColors />
      <main>
        {/* Hero */}
        <section className="pt-32 pb-16 bg-gradient-to-b from-orange-50 to-white relative overflow-hidden">
          <div className="absolute top-20 right-20 w-72 h-72 bg-coral/10 rounded-full blur-3xl" />
          <div className="section-container relative z-10">
            <span className="inline-block px-4 py-2 bg-coral/10 text-coral rounded-full text-sm font-semibold mb-4">
              Get In Touch
            </span>
            <h1 className="text-4xl md:text-6xl font-bold text-slate-900 font-['Outfit'] mb-6">
              Contact <span className="text-coral">Us</span>
            </h1>
            <p className="text-xl text-slate-600 max-w-2xl">
              Have questions about Apollo House? We're here to help. Reach out to us via phone, email, or fill out the form below.
            </p>
          </div>
        </section>

        {/* Contact Content */}
        <section className="section-spacing bg-white">
          <div className="section-container">
            <div className="grid lg:grid-cols-2 gap-12">
              {/* Contact Info */}
              <div>
                <h2 className="text-2xl font-bold text-slate-900 font-['Outfit'] mb-8">
                  Contact Information
                </h2>
                
                <div className="space-y-6">
                  {/* Phone */}
                  <div className="flex gap-4">
                    <div className="w-12 h-12 bg-coral/10 rounded-xl flex items-center justify-center shrink-0">
                      <Phone className="w-6 h-6 text-coral" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900 mb-1">Phone</h3>
                      <a href={`tel:${companyInfo.phone.tel}`} className="text-slate-600 hover:text-coral block">
                        Tel: {companyInfo.phone.tel}
                      </a>
                      <a
                        href={`https://wa.me/${companyInfo.phone.whatsapp}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-slate-600 hover:text-coral block"
                      >
                        WhatsApp: {companyInfo.phone.whatsappDisplay}
                      </a>
                    </div>
                  </div>

                  {/* Email */}
                  <div className="flex gap-4">
                    <div className="w-12 h-12 bg-coral/10 rounded-xl flex items-center justify-center shrink-0">
                      <Mail className="w-6 h-6 text-coral" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900 mb-1">Email</h3>
                      <a href={`mailto:${companyInfo.emails.general}`} className="text-slate-600 hover:text-coral block">
                        {companyInfo.emails.general}
                      </a>
                      <a href={`mailto:${companyInfo.emails.students}`} className="text-slate-600 hover:text-coral block">
                        {companyInfo.emails.students}
                      </a>
                    </div>
                  </div>

                  {/* Office Hours */}
                  <div className="flex gap-4">
                    <div className="w-12 h-12 bg-coral/10 rounded-xl flex items-center justify-center shrink-0">
                      <Clock className="w-6 h-6 text-coral" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900 mb-1">Office Hours</h3>
                      <p className="text-slate-600">Monday - Friday: 8:00 AM - 5:00 PM</p>
                      <p className="text-slate-600">Saturday: 9:00 AM - 1:00 PM</p>
                      <p className="text-slate-600">Sunday: Closed</p>
                    </div>
                  </div>
                </div>

                {/* Locations */}
                <h3 className="text-xl font-bold text-slate-900 font-['Outfit'] mt-10 mb-6">
                  Our Locations
                </h3>
                <div className="space-y-4">
                  {companyInfo.addresses.map((address, idx) => (
                    <div key={idx} className="flex gap-4 p-4 bg-orange-50 rounded-xl border border-orange-100">
                      <div className="w-10 h-10 bg-coral/20 rounded-lg flex items-center justify-center shrink-0">
                        <MapPin className="w-5 h-5 text-coral" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-slate-900">{address.name}</h4>
                        <p className="text-slate-600">{address.street}</p>
                        <p className="text-slate-600">{address.city}, {address.postalCode}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Contact Form */}
              <div>
                <div className="bg-orange-50 rounded-2xl p-8 border border-orange-100">
                  <h2 className="text-2xl font-bold text-slate-900 font-['Outfit'] mb-6">
                    Send Us a Message
                  </h2>
                  <ContactForm />
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default ContactPage;
